import Link from "next/link"
import { Routes } from "app/routes"
import { LabeledTextField } from "app/core/components/form/controls/LabeledTextField"
import { Form } from "app/core/components/form/Form"
import { Login } from "app/auth/validations"
import React, { useState } from "react"
import Card from "app/core/components/cards/Card"
import { isAppError } from "app/errors"
import type { PromiseReturnType } from "app/auth/types"
import { CsrfErrorHelp } from "app/auth/components/CsrfErrorHelp"
import { trpc } from "app/trpc/client"
import type login from "app/auth/mutations/login"
type LoginFormProps = {
  onSuccess?: (user: PromiseReturnType<typeof login>) => void
}

const ErrorDisplay = ({ error }: { error?: Error }) => {
  if (!error) {
    return <></>
  }
  if (isAppError(error, "CSRFTokenMismatchError")) {
    return <CsrfErrorHelp />
  }
  if (isAppError(error, "AuthenticationError")) {
    return <span className="text-danger">Sorry, those credentials are invalid</span>
  }
  return (
    <span className="text-danger">
      Sorry, we had an unexpected error. Please try again. - {error.message}
    </span>
  )
}

export const LoginForm = (props: LoginFormProps) => {
  const utils = trpc.useUtils()
  const { mutateAsync: loginMutation } = trpc.auth.login.useMutation()
  const [error, setError] = useState<Error | undefined>(undefined)

  return (
    <Card>
      <h1 className="h3">Please log in</h1>
      <Form
        submitText="Login"
        schema={Login}
        defaultValues={{ email: "", password: "" }}
        submitHandler={async (values) => {
          setError(undefined)
          try {
            const user = await loginMutation(values)
            await utils.users.getCurrentUser.invalidate()
            await props.onSuccess?.(user)
          } catch (error) {
            if (isAppError(error, "CSRFTokenMismatchError")) {
              // special handling of CSRF token problems
              try {
                await fetch(process.env.NEXT_PUBLIC_APP_ORIGIN + "/api/auth/reset-session")
                const user = await loginMutation(values)
                await utils.users.getCurrentUser.invalidate()
                props.onSuccess?.(user)
              } catch (error) {
                setError(error)
              }
            } else {
              setError(error)
            }
          }
        }}
        controls={({ control, formState }) => (
          <>
            <LabeledTextField
              control={control}
              name="email"
              label="Email"
              autoComplete="username"
              placeholder="Email"
              type="email"
            />
            <LabeledTextField
              control={control}
              name="password"
              label="Password"
              placeholder="Password"
              type="password"
              autoComplete="current-password"
            />
            <div className="text-end m-0 small">
              <Link href={Routes.ForgotPasswordPage()}>Forgot your password?</Link>
            </div>
            <div className="d-flex align-items-start">
              <ErrorDisplay error={error} />
              <button
                type="submit"
                className="btn btn-primary ms-auto flex-shrink-0"
                disabled={formState.isSubmitting}
              >
                Log in
              </button>
            </div>
          </>
        )}
      />
    </Card>
  )
}

export default LoginForm
